import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Circle, Loader2, RotateCcw, Sparkles, Undo2, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router'

import { ErrorDeLaApi } from '../api/nucleo'
import { Aviso, Boton, Cargando, clases, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from '../paginas/Modulo'
import { enMarcha, planes, useEstimacion, usePlan, usePlanes, type VistaDelPlan } from './api'
import { RevisionDelPlan } from './RevisionDelPlan'

/**
 * Estructura → «Proponer con IA» (plan del CMS dinámico §8.3): la instrucción, la espera mientras
 * la IA piensa, la revisión (lo que se crea, lo que cambia, lo que no se aplicará), la aplicación
 * con su progreso y el resultado con enlaces y «Deshacer». Todo lo decide el núcleo.
 */
export const ProponerConIA = () => {
  const { espacio, sitio, permisos } = useOutletContext<Contexto>()
  const cliente = useQueryClient()
  const sitioId = sitio?.id ?? null
  const { data: recientes } = usePlanes(sitioId)
  const { data: estimacion } = useEstimacion(sitioId)
  const [actual, setActual] = useState<null | number>(null)
  const [instruccion, setInstruccion] = useState('')
  const [pidiendo, setPidiendo] = useState(false)
  const [error, setError] = useState<null | string>(null)
  const [deshecho, setDeshecho] = useState<null | { conservados: Array<{ que: string; motivo: string }>; completo: boolean }>(null)
  const { data: vista } = usePlan(actual)

  // Al volver a la pantalla, se sigue el último plan que no ha terminado, o el último aplicado mientras se pueda
  // deshacer. Una vez: «Proponer otra» deja la pantalla en la instrucción, aunque haya uno que retomar.
  const [retomado, setRetomado] = useState(false)
  useEffect(() => {
    if (retomado || actual !== null || !recientes) return
    setRetomado(true)
    // La lista viene de la más nueva a la más vieja.
    const vivo = recientes.find((p) => p.origen === 'estructura' && (enMarcha(p.estado) || p.estado === 'propuesto' || (p.estado === 'aplicado' && p.sePuedeDeshacer)))
    if (vivo) setActual(vivo.id)
  }, [actual, recientes, retomado])

  // Al terminar de aplicarse, el menú (los tipos nuevos) y las listas se refrescan.
  useEffect(() => {
    if (vista?.estado === 'aplicado') void cliente.invalidateQueries({ queryKey: ['lista'] })
  }, [cliente, vista?.estado])

  if (!sitio) return <Aviso>Elige una web para organizar su estructura.</Aviso>
  if (!sitio.modulos?.includes('estructura')) return <Aviso>Esta web no tiene tipos de contenido a medida. Pídeselo al equipo o sube de plan.</Aviso>
  if (!permisos.tiene('ia.estructura')) return <Aviso>No tienes permiso para proponer la estructura con IA. Pídeselo al propietario de la cuenta.</Aviso>

  const base = `/e/${espacio}/s/${sitio.id}`
  const empezarOtra = () => {
    setActual(null)
    setDeshecho(null)
    setInstruccion('')
    void cliente.invalidateQueries({ queryKey: ['planes-de-estructura', sitio.id] })
  }
  const proponer = async () => {
    setPidiendo(true)
    setError(null)
    try {
      const v = await planes.proponer(sitio.id, instruccion)
      cliente.setQueryData(['plan-de-estructura', v.id], v)
      setActual(v.id)
    } catch (e) {
      setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido pedir la propuesta. Vuelve a intentarlo.')
    } finally {
      setPidiendo(false)
    }
  }
  const actualizar = (v: VistaDelPlan) => cliente.setQueryData(['plan-de-estructura', v.id], v)

  const titulo = (
    <Titulo descripcion="Cuéntale a la IA qué quieres organizar y te propone tipos de contenido, clasificaciones, campos, fichas y menús. Lo revisas antes de aplicarlo, y todo queda en borrador hasta que publiques." rotulo="Estructura">
      Proponer con IA
    </Titulo>
  )

  // Sin saldo para lo que cuesta, se dice antes y no se pide nada (§17): el núcleo, además, respondería 402.
  const sinSaldo = Boolean(estimacion?.disponible && estimacion.alcanza === false)

  if (!vista || actual === null) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-5">
        {titulo}
        <Tarjeta>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">¿Qué quieres organizar?</span>
            <textarea
              className="campo min-h-28 w-full rounded-campo border border-linea bg-papel px-3 py-2 text-sm hover:border-linea-fuerte"
              maxLength={2000}
              onChange={(e) => setInstruccion(e.target.value)}
              placeholder="Añade un tipo «Proyectos» con el cliente, el año y una galería, clasificados por área."
              value={instruccion}
            />
            <span className="text-xs text-tenue">Si lo dejas vacío, la IA propone lo que le falta a tu web a partir de lo que ya tiene.</span>
          </label>
          {estimacion && sinSaldo ? (
            <div className="mt-3">
              <Aviso tono="aviso">
                Proponer la estructura con IA cuesta como mucho {estimacion.creditos.toLocaleString('es')} créditos y tienes {estimacion.saldo.toLocaleString('es')}. No se ha pedido nada ni se te ha cobrado.{' '}
                <Link className="font-medium text-violeta underline" to={`/e/${espacio}/creditos`}>
                  Consigue más créditos
                </Link>{' '}
                o crea la estructura a mano en Tipos de contenido.
              </Aviso>
            </div>
          ) : (
            estimacion && (
              <p className="mt-3 text-xs text-tenue">
                {estimacion.disponible
                  ? `Proponerlo cuesta como mucho ${estimacion.creditos.toLocaleString('es')} créditos (tienes ${estimacion.saldo.toLocaleString('es')}). Aplicarlo no cuesta nada; si pide fichas con diseño propio, ese diseño se cobra aparte, como en Diseño.`
                  : 'Proponer la estructura con IA no está disponible ahora mismo.'}
              </p>
            )
          )}
          {error && (
            <div className="mt-3">
              <Aviso tono="error">{error}</Aviso>
            </div>
          )}
          <div className="mt-4">
            <Boton cargando={pidiendo} disabled={estimacion?.disponible === false || sinSaldo} icono={<Sparkles />} onClick={() => void proponer()} tipo="primario">
              Proponer
            </Boton>
          </div>
        </Tarjeta>
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      {titulo}
      {vista.instruccion && <p className="text-sm text-tenue">Pediste: «{vista.instruccion}»</p>}

      {vista.estado === 'pensando' && <Cargando que="La IA está pensando la estructura de tu web. Puede tardar un par de minutos; puedes irte y volver" />}

      {vista.estado === 'propuesto' && <RevisionDelPlan alResolver={(v) => (v.estado === 'descartado' ? empezarOtra() : actualizar(v))} vista={vista} />}

      {(vista.estado === 'aceptado' || vista.estado === 'aplicando' || vista.estado === 'aplicado' || (vista.estado === 'fallido' && vista.pasos.length > 0)) && (
        <Tarjeta>
          <h3 className="mb-3 font-display text-base font-semibold">{vista.estado === 'aplicado' ? 'Tu estructura está lista, en borrador' : vista.estado === 'fallido' ? 'Se ha quedado a medias' : 'Aplicando la estructura'}</h3>
          <ol className="flex flex-col gap-2">
            {vista.pasos.map((p) => (
              <li className="flex items-start gap-2.5 text-sm" key={p.clave}>
                <span aria-hidden className="mt-0.5">
                  {p.estado === 'hecho' ? <CheckCircle2 className="size-4 text-ok" /> : p.estado === 'fallido' ? <XCircle className="size-4 text-error" /> : p.estado === 'omitido' ? <Circle className="size-4 text-tenue" /> : enMarcha(vista.estado) ? <Loader2 className="size-4 animate-spin text-violeta" /> : <Circle className="size-4 text-tenue" />}
                </span>
                <span className="min-w-0">
                  <span className={clases(p.estado === 'omitido' && 'text-tenue')}>{p.rotulo}</span>
                  {p.mensaje && <span className="block text-xs text-tenue">{p.mensaje}</span>}
                </span>
              </li>
            ))}
          </ol>
        </Tarjeta>
      )}

      {vista.estado === 'aplicado' && vista.creados && (
        <Tarjeta>
          <h3 className="mb-2 font-display text-base font-semibold">Qué se ha creado</h3>
          <ul className="flex flex-col gap-1.5 text-sm">
            {Object.entries(vista.creados.tipos).map(([slug, id]) => (
              <li key={`t-${slug}`}>
                Tipo «{vista.plan?.tipos.find((t) => t.slug === slug)?.nombrePlural ?? slug}»:{' '}
                <Link className="text-violeta underline" to={`${base}/tipos-de-contenido/${id}`}>
                  su estructura
                </Link>{' '}
                ·{' '}
                <Link className="text-violeta underline" to={`${base}/contenidos/${id}`}>
                  sus contenidos
                </Link>
              </li>
            ))}
            {Object.entries(vista.creados.taxonomias).map(([slug, id]) => (
              <li key={`x-${slug}`}>
                Clasificación «{vista.plan?.taxonomias.find((t) => t.slug === slug)?.nombrePlural ?? slug}»:{' '}
                <Link className="text-violeta underline" to={`${base}/taxonomias/${id}/terminos`}>
                  sus términos
                </Link>
              </li>
            ))}
            {Object.entries(vista.creados.grupos).map(([nombre, id]) => (
              <li key={`g-${nombre}`}>
                Campos «{nombre}»:{' '}
                <Link className="text-violeta underline" to={`${base}/grupos-de-campos/${id}`}>
                  abrir
                </Link>
              </li>
            ))}
            {Object.entries(vista.creados.paginas).map(([slug, id]) => (
              <li key={`p-${slug}`}>
                Página «{vista.plan?.paginas.find((p) => p.slug === slug)?.titulo ?? slug}»:{' '}
                <Link className="text-violeta underline" to={`${base}/paginas/${id}`}>
                  abrir
                </Link>
              </li>
            ))}
            {vista.version && (
              <li>
                El diseño de las fichas, en la versión {vista.version}:{' '}
                <Link className="text-violeta underline" to={`${base}/diseno`}>
                  verlo en Diseño
                </Link>
              </li>
            )}
          </ul>
          {vista.creados.avisos.map((a) => (
            <div className="mt-3" key={a}>
              <Aviso tono="info">{a}</Aviso>
            </div>
          ))}
          <p className="mt-3 text-xs text-tenue">Nada se ve en tu web hasta que lo publiques con «Publicar mi web».</p>
        </Tarjeta>
      )}

      {vista.estado === 'fallido' && vista.mensaje && <Aviso tono="error">{vista.mensaje}</Aviso>}
      {vista.estado === 'deshecho' && <Aviso tono="ok">La estructura se ha deshecho: lo que creó se ha borrado.</Aviso>}
      {deshecho && !deshecho.completo && (
        <Aviso>
          Se ha deshecho una parte. Se queda: {deshecho.conservados.map((c) => `${c.que} (${c.motivo.replace(/\.$/, '')})`).join('; ')}.
        </Aviso>
      )}

      <div className="flex flex-wrap gap-2">
        {vista.estado === 'fallido' && vista.pasos.length > 0 && (
          <Boton
            icono={<RotateCcw />}
            onClick={() =>
              void planes
                .aceptar(vista.id, { quitar: [], renombrar: {} })
                .then(actualizar)
                .catch((e: unknown) => setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido reintentar.'))
            }
            tipo="primario"
          >
            Reintentar
          </Boton>
        )}
        {vista.sePuedeDeshacer && (
          <Boton
            icono={<Undo2 />}
            onClick={() => {
              if (!window.confirm('¿Deshacer esta estructura? Se borra lo que creó y nadie ha tocado; lo que ya tiene contenido se queda.')) return
              void planes
                .deshacer(vista.id)
                .then((r) => {
                  setDeshecho({ conservados: r.conservados, completo: r.completo })
                  actualizar(r.plan)
                  void cliente.invalidateQueries({ queryKey: ['lista'] })
                })
                .catch((e: unknown) => setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido deshacer.'))
            }}
            tipo="peligro"
          >
            Deshacer
          </Boton>
        )}
        {!enMarcha(vista.estado) && vista.estado !== 'propuesto' && (
          <Boton icono={<Sparkles />} onClick={empezarOtra}>
            Proponer otra
          </Boton>
        )}
      </div>
      {error && <Aviso tono="error">{error}</Aviso>}
    </div>
  )
}
